import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram, ShieldCheck, Droplets, Feather, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const IG_URL = process.env.REACT_APP_INSTAGRAM_URL;
const IG_HANDLE = process.env.REACT_APP_INSTAGRAM_HANDLE;

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/products").then(r => setFeatured(r.data)).catch(() => {});
    fetch("/gallery.json").then(r => r.json()).then((d) => setGallery(Array.isArray(d) ? d : (d.gallery || []))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo />
      {/* HERO — full-width photo with overlay copy (taller aspect on phones so copy has room) */}
      <section
        data-testid="hero-band"
        className="relative w-full overflow-hidden aspect-[4/5] md:aspect-[2000/1052] min-h-[560px] md:min-h-0"
        style={{ background: "var(--kaiya-text)" }}
      >
        <img
          src="/assets/hero_beach.jpg"
          alt="Three friends sitting on Kaiya towels"
          className="absolute inset-0 w-full h-full object-cover block"
          style={{ objectPosition: "center center" }}
          fetchPriority="high"
          decoding="async"
        />
        {/* bottom vignette so copy over the mats stays legible on every viewport */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 100%)",
          }}
        />

        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-16 flex items-end pb-8 md:pb-14">
          <div className="max-w-2xl hero-copy">
            <div className="label-eyebrow mb-3 hero-eyebrow text-[10px] md:text-xs">
              Female-founded · Artist-driven
            </div>
            <h1 className="font-serif-display text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] mb-4 hero-headline">
              your towel<br />
              <span className="italic">a space to create</span>
            </h1>
            <p className="text-xs sm:text-sm md:text-base mb-6 max-w-lg hero-body">
              Through art and intention, we create spaces that inspire creativity, possibility, and growth.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
              <Link
                to="/products"
                data-testid="hero-shop-btn"
                className="btn-primary inline-flex items-center justify-center gap-2 text-sm md:text-base py-3 px-6"
              >
                Shop the towels <ArrowRight size={16} />
              </Link>
              <Link
                to="/artist"
                data-testid="hero-artist-btn"
                className="rounded-full px-6 py-3 text-sm md:text-base font-medium text-center transition-colors duration-300 border-2 border-white text-white hover:bg-white/10"
                style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
              >
                Meet the artist
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO STRIP */}
      <section className="py-12 md:py-24 text-center px-6" style={{ background: "var(--kaiya-primary)", color: "var(--kaiya-bg)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="label-eyebrow mb-4 md:mb-6" style={{ color: "rgba(249,246,240,0.6)" }}>The invitation</div>
          <p className="font-serif-display text-xl sm:text-2xl md:text-4xl leading-tight">
            KaiyaYoga is more than a brand — it&apos;s an invitation to{" "}
            <em style={{ color: "var(--kaiya-bg-2)" }}>release what no longer serves you</em>{" "}
            and embrace what fuels your growth, on and off the mat.
          </p>
          <div className="mt-10 md:mt-14 font-serif-display text-2xl sm:text-3xl md:text-4xl leading-[0.95]" data-testid="invitation-mantra">
            let go.<br />
            <span style={{ color: "var(--kaiya-bg-2)" }}>let&apos;s grow.</span>
          </div>
        </div>
      </section>

      {/* THE COLLECTION */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 lg:py-32">
        <div className="text-center mb-10 md:mb-16">
          <div className="label-eyebrow mb-3">Your Towel</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-none mb-6">
            Three canvases.<br />One practice.
          </h2>
          <p className="max-w-xl mx-auto text-sm md:text-base opacity-70">
            Each towel is an original monotype by Toronto artist Kelsey-Lynn. A portion of every purchase directly supports the artist.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-12">
          {featured.slice(0, 3).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* SECOND HERO — 3 towels over lake */}
      <section className="relative h-[70vh] md:h-[85vh] overflow-hidden mb-24 md:mb-32">
        <img
          src="/assets/towels_lake.jpg"
          alt="Three Kaiya towels over the lake"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </section>

      {/* ABOUT THE TOWEL */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-3">About your towel</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-6">
              A space to<br />create.
            </h2>
            <p className="opacity-80 leading-relaxed">
              A microfibre waffle canvas that grips when you sweat, wicks when you flow, and folds soft into your bag. Machine wash cold, tumble dry low. 80% polyester / 20% nylon.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
            {[
              { icon: ShieldCheck, label: "Anti-slip grip" },
              { icon: Droplets, label: "Sweat absorbent" },
              { icon: Feather, label: "Gentle on skin" },
              { icon: Sparkles, label: "Odour resistant" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="kaiya-card p-6 md:p-8" data-testid={`feature-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={26} style={{ color: "var(--kaiya-primary)" }} className="mb-4" />
                <div className="font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET US ON YOUR MAT */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32" data-testid="meet-us-section">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 image-soft aspect-[3/4] max-w-md mx-auto md:mx-0 overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <img src="/assets/packaging_sand.jpg" alt="Kaiya packaging on the sand" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
          <div className="md:col-span-6">
            <div className="label-eyebrow mb-3">Come practice</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-none mb-6">
              Meet us on<br /><span style={{ color: "var(--kaiya-primary)" }}>your mat.</span>
            </h2>
            <p className="opacity-80 leading-relaxed mb-8">
              Every towel is a place to arrive as you are. Roll it out, breathe in, and let the practice meet you.
            </p>
            <a
              href={IG_URL}
              target="_blank" rel="noreferrer"
              data-testid="ig-cta"
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Instagram size={16} /> @{IG_HANDLE}
            </a>
          </div>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <InstagramFeed />
    </div>
  );
}

function InstagramFeed() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/instagram/posts").then(r => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32" data-testid="ig-feed-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 md:mb-12">
        <div>
          <div className="label-eyebrow mb-3">The feed</div>
          <h2 className="font-serif-display text-3xl md:text-5xl leading-none">@{IG_HANDLE}</h2>
        </div>
        <a
          href={IG_URL}
          target="_blank" rel="noreferrer"
          className="btn-secondary inline-flex items-center gap-2 self-start"
          data-testid="ig-feed-follow"
        >
          <Instagram size={16} /> Follow
        </a>
      </div>

      {posts.length === 0 ? (
        <a
          href={IG_URL}
          target="_blank" rel="noreferrer"
          className="block kaiya-card p-12 md:p-16 text-center hover:opacity-90 transition"
          style={{ background: "var(--kaiya-bg-2)" }}
          data-testid="ig-feed-empty"
        >
          <Instagram size={32} className="mx-auto mb-4" style={{ color: "var(--kaiya-primary)" }} />
          <p className="font-serif-display text-2xl mb-2">Follow us for the latest</p>
          <p className="opacity-70 text-sm">@{IG_HANDLE} on Instagram</p>
        </a>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {posts.slice(0, 8).map((p, i) => (
            <a
              key={p.id}
              href={p.post_url || IG_URL}
              target="_blank" rel="noreferrer"
              data-testid={`ig-feed-post-${i}`}
              className="image-soft aspect-square block overflow-hidden"
              title={p.caption}
            >
              <img src={p.image_url} alt={p.caption || "instagram"} className="w-full h-full object-cover hover:opacity-80 transition duration-500" loading="lazy" />
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
